import { api } from "./client";

// Public-safe shape of one state's transitioning action — `when`
// predicates are stripped server-side, so the frontend probes
// authorization via /auth/is_authorized for each candidate before
// rendering it.
export interface WorkflowAction {
  name: string;
  description?: string;
  button_label?: string;
  next_state?: string;
  version_history_label?: string;
}

// A role responsible for acting on a state, with backend-owned display
// copy (from ROLE_LABELS) so the canvas doesn't keep its own role->label map.
export interface ResponsibleRole {
  role: string;
  label: string;
}

export interface WorkflowState {
  label: string;
  category: string;
  short_label?: string;
  timeline_label?: string;
  actions?: WorkflowAction[];
  // Names of the side-effect callbacks the backend fires when a
  // suggestion *enters* this state (notification hooks). The View
  // Workflow canvas surfaces these as the "Happens automatically" list.
  on_enter?: string[];
  // Roles responsible for acting on this state — the union of the role gates
  // across its actions' `when` clauses (admins excluded), derived server-side
  // before the predicates are stripped. The canvas renders them per step.
  responsible_roles?: ResponsibleRole[];
}

// One assignable seat on a committee. `key` is what the
// `assign_reviewers` action takes as `slot`; `sub_role` is the vocabulary
// the stored AssignedReviewer carries.
export interface WorkflowCommitteeSlot {
  key: string;
  label: string;
  sub_role: string;
}

export interface WorkflowCommittee {
  label: string;
  slots: WorkflowCommitteeSlot[];
}

export interface WorkflowConfig {
  name: string;
  initial_state: string;
  happy_path: string[];
  states: Record<string, WorkflowState>;
  // Committee seats the custodian fills via `assign_reviewers`, keyed by
  // committee token ("wc" / "sc"). Backend-owned so the frontend keeps no
  // committee or slot list of its own; absent on older backends.
  committees?: Record<string, WorkflowCommittee>;
  // Facets ride on the same payload so the breakdown + filter UI can
  // read status_display_order / facet labels from one source.
  facets?: WorkflowFacet[];
  // Human-readable copy for the `on_enter` hook names referenced across
  // states (hook name -> label). Backend-owned so the canvas doesn't
  // hardcode notification labels of its own.
  callback_labels?: Record<string, string>;
}

export interface WorkflowFacetStatus {
  name: string;
  label: string;
  // The workflow states this status groups, read server-side off its
  // `when: {in: {item: $state.name, set: [...]}}`. Absent when the
  // predicate is anything richer (`when: true` on "all") and on backends
  // that predate the field.
  states?: string[];
}

export interface WorkflowFacet {
  name: string;
  label: string;
  status_display_order?: string[];
  statuses: WorkflowFacetStatus[];
}

export interface WorkflowFacetsResponse {
  workflow: string;
  facets: WorkflowFacet[];
}

export interface TimelineEntry {
  state: string;
  label: string;
  is_happy_path: boolean;
  is_current: boolean;
  states: string[];
}

export interface TimelineResponse {
  current_state: string;
  timeline: TimelineEntry[];
}

export async function getWorkflowConfig(
  workflowName: string,
): Promise<WorkflowConfig> {
  const { data } = await api.get<WorkflowConfig>(
    `/auth/workflow/${encodeURIComponent(workflowName)}`,
  );
  return data;
}

export async function getWorkflowFacets(
  workflowName: string,
): Promise<WorkflowFacetsResponse> {
  const { data } = await api.get<WorkflowFacetsResponse>(
    `/auth/workflow/${encodeURIComponent(workflowName)}/facets`,
  );
  return data;
}

export async function getTimeline(
  workflowName: string,
  entityType: string,
  entityId: string,
): Promise<TimelineResponse> {
  const { data } = await api.post<TimelineResponse>("/auth/get_timeline", {
    workflow_name: workflowName,
    entity_type: entityType,
    entity_id: entityId,
  });
  return data;
}
